'use client';

import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Search } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useMarketplaceStore, MarketplaceState } from '@/store/marketplace-store';
import { useAddressStore } from '@/store/address-store';
import Image from 'next/image';
interface Place {
  name: string;
  formatted_address: string;
  place_id: string;
  geometry?: {
    location: {
      lat: number;
      lng: number;
    }
  }
}

const Hero = () => {
  const { config } = useMarketplaceStore();
  const marketplaceStore = useMarketplaceStore();
  const {setAddress, getAddress} = useAddressStore();
  const selectedAddress = getAddress();
  const router = useRouter();
  const setLocation = useMarketplaceStore().setLocation;
  // Initialize state with empty values
  const [searchInput, setSearchInput] = useState('');
  const [places, setPlaces] = useState<Place[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState<Place | null>(null);
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);
  const initialized = useRef(false);
  // Modified useEffect to prevent infinite loops
  useEffect(() => {
    if (initialized.current) return;

    const currentAddress = getAddress();
    const currentLat = marketplaceStore.getConfigItem("latitude", "");
    const currentLng = marketplaceStore.getConfigItem("longitude", "");
    
    if (currentAddress) {
      initialized.current = true;
      setSearchInput(currentAddress);
      
      // Create a place object with the current coordinates
      if (currentLat && currentLng) {
        const currentPlace: Place = {
          name: currentAddress,
          formatted_address: currentAddress,
          place_id: 'current',
          geometry: {
            location: {
              lat: parseFloat(currentLat),
              lng: parseFloat(currentLng)
            }
          }
        };
        setSelectedPlace(currentPlace);
      }
      return;
    }

    // If no current address, handle stored default address
    const storedAddress = marketplaceStore.getConfigItem("address", "");
    const storedLat = marketplaceStore.getConfigItem("latitude", "");
    const storedLng = marketplaceStore.getConfigItem("longitude", "");

    if (storedAddress && storedLat && storedLng) {
      initialized.current = true;
      setSearchInput(storedAddress);
      
      const storedPlace: Place = {
        name: storedAddress,
        formatted_address: storedAddress,
        place_id: 'stored',
        geometry: {
          location: {
            lat: parseFloat(storedLat),
            lng: parseFloat(storedLng)
          }
        }
      };
      
      setSelectedPlace(storedPlace);
      setLocation(parseFloat(storedLat), parseFloat(storedLng), storedAddress);
      setAddress(storedAddress, {
        latitude: parseFloat(storedLat), 
        longitude: parseFloat(storedLng)
      });
    }
  }, []); 

  const handleInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchInput(value);

    // Clear existing timeout
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    if (value.length >= 3) {
      // Add debounce to avoid too many API calls
      searchTimeout.current = setTimeout(async () => {
        let key = config?.settings?.webapp_google_api_key || "";
        try {
          const response = await fetch(
            `/api/places/search?query=${encodeURIComponent(value)}&key=${key}`
          );
          const data = await response.json();
          setPlaces(data.results || []);
          setShowDropdown(true);
        } catch (error) {
          setPlaces([]);
          setShowDropdown(false);
        }
      }, 300);
    } else {
      setPlaces([]);
      setShowDropdown(false);
    }
  };

  const handlePlaceSelect = (place: Place) => {
    setSearchInput(place.formatted_address);
    setSelectedPlace(place);
    setShowDropdown(false);
    // Store location data in marketplace store when a place is selected
    if (place.geometry?.location) {
      const { lat, lng } = place.geometry.location;
      setLocation(lat, lng, place.formatted_address);
      setAddress(place.formatted_address, {latitude: lat, longitude: lng})
    }
  };

  const handleFindFood = () => {
    if (selectedPlace?.geometry?.location) {
      router.push('/business-category');
    } else {
      alert('Please select a location first');
    }
  };

  // Get color and background from marketplace config or use defaults
  const primaryColor = config?.colors?.primary || '#900f21';
  const backgroundImage = config?.background_image || '/restaurant-bg.jpg';

  return (
  <section 
    className="relative min-h-[80vh] flex items-center"
  >
    <Image 
      src={backgroundImage} 
      alt="Background Image" // Add this line
      layout="fill" 
      objectFit="cover" 
      objectPosition="center" 
    />

      <div className="absolute inset-0 z-0 bg-gradient-to-r from-black/60 to-black/30"></div>
      
      <div className="container relative z-10 px-4 py-12 mx-auto md:py-20">
        <div className="max-w-3xl">
          <div>
            <h1 className="mb-4 text-3xl font-bold text-white md:text-4xl lg:text-5xl">
              Premium Dining <span style={{ color: primaryColor }}>Delivered</span> To Your Doorstep
            </h1>
            
            <p className="mb-8 text-lg text-gray-200">
              Experience the luxury of Qmin hospitality at home. Order from your favorite 
              Taj hotels and restaurants with just a few taps.
            </p>
          </div>
          
          <div className="flex flex-col gap-4 mb-8 sm:flex-row">
            <div className="relative flex-1 min-w-0">
              <input
                type="text"
                id="location-input"
                placeholder="Enter your location to find restaurants"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none"
                value={searchInput}
                onChange={handleInputChange}
              />
              <div 
                className="absolute p-1 transform -translate-y-1/2 rounded-full right-3 top-1/2"
                style={{ backgroundColor: primaryColor }}
              >
                <Search className="w-5 h-5 text-white" />
              </div>

              {/* Dropdown for search results */}
              {showDropdown && places.length > 0 && (
                <div className="absolute z-20 w-full mt-1 overflow-y-auto bg-white rounded-lg shadow-lg max-h-60">
                  {places.map((place) => (
                    <div
                      key={place.place_id}
                      className="px-4 py-2 cursor-pointer hover:bg-gray-100"
                      onClick={() => handlePlaceSelect(place)}
                    >
                      <div className="font-medium">{place.name}</div>
                      <div className="text-sm text-gray-600">{place.formatted_address}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <Button 
              className="px-6 py-3 font-medium text-white rounded-lg hover:opacity-90 h-12 text-base"
              style={{ backgroundColor: primaryColor }}
              onClick={handleFindFood}
            >
              Find Food
            </Button>
          </div>
          
          {/* <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center px-4 py-2 rounded-full bg-white/10 backdrop-blur-md">
              <span style={{ color: primaryColor }} className="mr-2 font-bold">600+</span>
              <span className="text-sm text-white">Restaurants</span>
            </div>
            <div className="flex items-center px-4 py-2 rounded-full bg-white/10 backdrop-blur-md">
              <span style={{ color: primaryColor }} className="mr-2 font-bold">25k+</span>
              <span className="text-sm text-white">Happy Customers</span>
            </div>
            <div className="flex items-center px-4 py-2 rounded-full bg-white/10 backdrop-blur-md">
              <span style={{ color: primaryColor }} className="mr-2 font-bold">4.8</span>
              <span className="text-sm text-white">App Rating</span>
            </div>
          </div> */}
        </div>
      </div>
    </section>
  );
};

export default Hero;
